import React from 'react';
import { motion } from 'framer-motion';
import { Search, Filter, FileSpreadsheet, CheckCircle2, TrendingUp, MessageSquare, DollarSign, Link as LinkIcon, Globe, Users, Mail, BadgeCheck, ChevronDown, Building2, Copy } from 'lucide-react';
// Platform Icons
const TTIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1248.png" alt="TikTok" className="w-3.5 h-3.5 inline-block" data-id="element-2009" data-mp="2nfrx14ui931p" />;
const IGIco = () => <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/362-1249.png" alt="Instagram" className="w-3.5 h-3.5 inline-block" data-id="element-2010" data-mp="1urvz5wt7953c" />;
const WindowBar = ({
  title,
  subtitle
}: {
  title: string;
  subtitle?: string;
}) => <div className="flex items-center gap-2 px-4 py-3 border-b border-gray-100 bg-gray-50/70" data-id="element-2011" data-mp="24npajcv6msjj">
    <div className="flex items-center gap-1.5" data-id="element-2012" data-mp="1gxuwwt31gzy8">
      <span className="w-2.5 h-2.5 rounded-full bg-red-400/70" data-id="element-2013" data-mp="1c1o13t4cx4u0" />
      <span className="w-2.5 h-2.5 rounded-full bg-amber-400/70" data-id="element-2014" data-mp="1zm8wltf5sj9b" />
      <span className="w-2.5 h-2.5 rounded-full bg-emerald-400/70" data-id="element-2015" data-mp="1zlr0qxwes71x" />
    </div>
    <span className="ml-2 text-xs font-semibold text-gray-600" data-id="element-2016" data-mp="23msvp2jalwy3">{title}</span>
    {subtitle && <span className="ml-auto text-[10px] text-gray-400" data-id="element-2017" data-mp="28flyhkv7av7j">{subtitle}</span>}
  </div>;
const floatY = (delay = 0, distance = 8) => ({
  animate: {
    y: [0, -distance, 0]
  },
  transition: {
    duration: 4,
    repeat: Infinity,
    ease: 'easeInOut' as const,
    delay
  }
});
// Shared generic creator data (photos + names + niches) reused across mockups.
const CREATORS = [{
  name: 'Elena Rodriguez',
  niche: 'Fashion',
  fol: '1.2M',
  views: '340K',
  eng: '4.2%',
  photo: 'https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Marcus Chen',
  niche: 'Tech',
  fol: '850K',
  views: '120K',
  eng: '5.1%',
  photo: 'https://images.unsplash.com/photo-1506794778202-cad84cf45f1d?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Sarah Jenkins',
  niche: 'Lifestyle',
  fol: '450K',
  views: '89K',
  eng: '3.8%',
  photo: 'https://images.unsplash.com/photo-1544005313-94ddf0286df2?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'David Kim',
  niche: 'Gaming',
  fol: '2.1M',
  views: '800K',
  eng: '6.4%',
  photo: 'https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=80&h=80&q=80'
}, {
  name: 'Aisha Kohl',
  niche: 'Beauty',
  fol: '640K',
  views: '150K',
  eng: '4.9%',
  photo: 'https://images.unsplash.com/photo-1487412720507-e7ab37603c6f?auto=format&fit=crop&w=80&h=80&q=80'
}];
export const LayeredRosterMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2018" data-mp="1ewyasswj0buf">
      <div className="absolute -inset-6 bg-gradient-to-tr from-orange-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2019" data-mp="1evn6mrz6522s" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2020" data-mp="3llqh9nb80k0u">
        <WindowBar title="My Roster" data-id="element-2021" data-mp="3qcyrumoaebfl" />

        {/* Toolbar */}
        <div className="p-3 border-b border-gray-100 flex items-center justify-between bg-white" data-id="element-2022" data-mp="3bnsul490evac">
          <div className="flex items-center gap-2" data-id="element-2023" data-mp="3glucvmym2qkr">
            <div className="relative w-48" data-id="element-2024" data-mp="3gmnb1zji87ra">
              <Search size={13} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2025" data-mp="862bf9crir0u" />
              <input type="text" placeholder="Search roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[11px] outline-none bg-gray-50" data-id="element-2026" data-mp="d4vox1s1ui10" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2.5 py-1.5 rounded-md text-[11px] font-medium" data-id="element-2027" data-mp="2jvld61lojeuw">
              <Filter size={12} data-id="element-2028" data-mp="2nz16anyjix2u" /> Filter
            </button>
          </div>
          <button className="bg-gray-900 text-white px-3 py-1.5 rounded-md text-[11px] font-bold" data-id="element-2029" data-mp="345mjtd0s7ji3">
            + Add Creator
          </button>
        </div>

        {/* Table */}
        <div className="flex-1 overflow-hidden bg-gray-50/30" data-id="element-2030" data-mp="2ue9v2uaa59ga">
          <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-2031" data-mp="2pfyjae0fcovf">
            <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-2032" data-mp="2pdvir01nbdcg">
              <tr data-id="element-2033" data-mp="3n2xhwuaog6vy">
                <th className="px-4 py-2.5 font-semibold" data-id="element-2034" data-mp="3n1cnd4od4ny3">Creator</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2035" data-mp="3i55fh6cep2di">Niche</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2036" data-mp="3d4rhkh4ir8t7">Followers</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2037" data-mp="393xrpi7injxo">Avg Views</th>
                <th className="px-4 py-2.5 font-semibold" data-id="element-2038" data-mp="3960iw5kenj3z">Eng. Rate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 bg-white" data-id="element-2039" data-mp="21o17gmwz5wub">
              {CREATORS.map((row, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-2040" data-mp="d6qajq7uzuch" data-mpi={i}>
                  <td className="px-4 py-2.5 font-medium text-gray-900 flex items-center gap-2" data-id="element-2041" data-mp="d8b3xcift78r">
                    <img src={row.photo} alt={row.name} className="w-5 h-5 rounded-full object-cover bg-gray-200" data-id="element-2042" data-mp="h8uzwcr9y4tb" />
                    {row.name}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-2043" data-mp="vxhnt2uyw4w5">{row.niche}</td>
                  <td className="px-4 py-2.5 text-gray-900 font-medium" data-id="element-2044" data-mp="vzkcnjkdvrza">
                    {row.fol}
                  </td>
                  <td className="px-4 py-2.5 text-gray-600" data-id="element-2045" data-mp="14xe9qwpz2pni">{row.views}</td>
                  <td className="px-4 py-2.5 text-emerald-600 font-medium" data-id="element-2046" data-mp="14w322lteoxr4">
                    {row.eng}
                  </td>
                </tr>)}
            </tbody>
          </table>
        </div>
      </div>

      {/* Floating Bulk Upload */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-2047" data-mp="3bovud9l0xo8q">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2048" data-mp="muz1d91d8axn">
          <div className="flex items-center gap-2 mb-1.5" data-id="element-2049" data-mp="wtoaodf74n2a">
            <div className="p-1.5 bg-green-50 rounded-md" data-id="element-2050" data-mp="wt52vpj9ff1z">
              <FileSpreadsheet size={14} className="text-green-600" data-id="element-2051" data-mp="1alkwy0u6o2qr" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2052" data-mp="16jpzj7fdi9i9">Bulk Upload</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2053" data-mp="1kj8wd42qhyf9">
            Import your entire roster in seconds using Google Sheets.
          </p>
        </motion.div>
      </motion.div>

      {/* Floating Filters */}
      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-5 -left-4 sm:-left-6 z-20" data-id="element-2054" data-mp="1z5enaft86zka">
        <motion.div {...floatY(0.9)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-2055" data-mp="1u8o6fq9ljzxt">
          <p className="text-[10px] font-bold text-gray-900 mb-2 flex items-center gap-1" data-id="element-2056" data-mp="1l7q2t932lufr">
            <Filter size={10} className="text-zano-pink" data-id="element-2057" data-mp="16iturc10khf5" /> Active Filters
          </p>
          <div className="flex flex-wrap gap-1.5" data-id="element-2058" data-mp="1bhes93ovsmzw">
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2059" data-mp="xgkjul1u2m18">
              Followers &gt; 500K
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2060" data-mp="11hnzp7jvsi2g">
              US-based
            </span>
            <span className="px-2 py-0.5 bg-gray-100 text-gray-600 rounded-full text-[9px] font-medium" data-id="element-2061" data-mp="1cylu2e02qfwl">
              Tech & Gaming
            </span>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredSubmissionsMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2062" data-mp="132in8uua1t3b">
      <div className="absolute -inset-6 bg-gradient-to-tr from-sky-100/40 via-zano-lavender/30 to-zano-pink/10 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2063" data-mp="yz2sk41rq2t9" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2064" data-mp="u617feayx718">
        <WindowBar title="Submit to Casting Call" subtitle="Summer Skincare Launch" data-id="element-2065" data-mp="kag8xq53yow8" />

        <div className="p-4 space-y-4" data-id="element-2066" data-mp="f9s8yeywixpv">
          <div className="bg-gray-50 border border-gray-200 rounded-xl p-3" data-id="element-2067" data-mp="fcxy1u7j5zll">
            <h4 className="text-xs font-bold text-gray-900 mb-1" data-id="element-2068" data-mp="b9rxn373n2n1">
              Select from your roster
            </h4>
            <div className="flex items-center gap-2 mb-3" data-id="element-2069" data-mp="2myoa2tp2sn5f">
              <div className="relative flex-1" data-id="element-2070" data-mp="2qx5m9rncsntp">
                <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2071" data-mp="2r01j1cngt6gk" />
                <input type="text" placeholder="Search..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] outline-none bg-white" data-id="element-2072" data-mp="30v1uobowp4o4" />
              </div>
              <button className="flex items-center gap-1 border border-zano-pink/30 bg-zano-blush text-zano-pink px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-2073" data-mp="2d6s9nbzioky1">
                <Filter size={10} data-id="element-2074" data-mp="2hytjysd4k1lf" /> Filter
              </button>
            </div>

            <div className="space-y-2" data-id="element-2075" data-mp="1khbnux6ek2as">
              <div className="flex items-center justify-between bg-white border border-zano-pink/40 rounded-lg p-2 shadow-sm" data-id="element-2076" data-mp="1kga84fefyrmq">
                <div className="flex items-center gap-2" data-id="element-2077" data-mp="pc38n7h7e4n2">
                  <img src="https://images.unsplash.com/photo-1524504388940-b1c1722653e1?auto=format&fit=crop&w=40&h=40" alt="" className="w-7 h-7 rounded-full object-cover" data-id="element-2078" data-mp="pdo3ky7fjplg" />
                  <div data-id="element-2079" data-mp="147sun5me2hrs">
                    <p className="text-[11px] font-bold text-gray-900" data-id="element-2080" data-mp="148c5619pm3vs">
                      Elena Rodriguez
                    </p>
                    <p className="text-[9px] text-gray-500" data-id="element-2081" data-mp="bk6ih8z3i4tb">
                      1.2M Followers • 340K Avg Views
                    </p>
                  </div>
                </div>
                <CheckCircle2 size={16} className="text-zano-pink" data-id="element-2082" data-mp="gh6qhpfi9hn2" />
              </div>
            </div>
          </div>

          <div className="space-y-3" data-id="element-2083" data-mp="3k08bheln0jl1">
            <div data-id="element-2084" data-mp="3jz6zn7wsrt29">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2085" data-mp="3jzq69sgzyv0z">
                Your Quote
              </label>
              <div className="mt-1 relative" data-id="element-2086" data-mp="263oxvv991pmv">
                <DollarSign size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2087" data-mp="263f4ryswr2cy" />
                <input type="text" value="2,500" readOnly className="w-full pl-7 pr-3 py-2 border border-gray-200 rounded-md text-xs font-medium bg-gray-50" data-id="element-2088" data-mp="1x2qwzlu3xy8i" />
              </div>
            </div>
            <div data-id="element-2089" data-mp="1iemfumapduru">
              <label className="text-[10px] font-semibold text-gray-500 uppercase tracking-wider" data-id="element-2090" data-mp="1ie492vqezy5o">
                Notes for Brand
              </label>
              <textarea readOnly className="mt-1 w-full px-3 py-2 border border-gray-200 rounded-md text-[11px] text-gray-700 bg-gray-50 h-16 resize-none" value="Elena has a highly engaged US female audience. She recently did a skincare campaign that performed 2x above average." data-id="element-2091" data-mp="1eaohidt7cnzu" />
            </div>
            <button className="w-full bg-zano-pink text-white rounded-md py-2 text-xs font-bold shadow-sm" data-id="element-2092" data-mp="pquwmecxijdp">
              Submit Creator
            </button>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-3 -left-4 sm:-left-6 z-20" data-id="element-2093" data-mp="gs9bcbg2oq98">
        <motion.div {...floatY(0.3, 6)} className="bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-44" data-id="element-2094" data-mp="zqbx8hdbq72t">
          <p className="text-[10px] font-bold text-gray-900 mb-1.5 flex items-center gap-1" data-id="element-2095" data-mp="13paynrzujlpi">
            <Filter size={10} className="text-zano-pink" data-id="element-2096" data-mp="1rf5cabrzpeat" /> Roster Filter
            Applied
          </p>
          <div className="space-y-1" data-id="element-2097" data-mp="1w9t2cf2n6gri">
            <div className="flex justify-between text-[9px]" data-id="element-2098" data-mp="3gxlptsb534ya">
              <span className="text-gray-500" data-id="element-2099" data-mp="3gw0uw1kwxjzw">Followers</span>
              <span className="font-semibold text-gray-900" data-id="element-2100" data-mp="3byireqpnzwza">&gt; 50K</span>
            </div>
            <div className="flex justify-between text-[9px]" data-id="element-2101" data-mp="3qmvhx85zgnme">
              <span className="text-gray-500" data-id="element-2102" data-mp="3lpdkadxf1utp">Avg Views</span>
              <span className="font-semibold text-gray-900" data-id="element-2103" data-mp="3lpvjnjfdkp5u">&gt; 100K</span>
            </div>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -right-4 sm:-right-6 z-20" data-id="element-2104" data-mp="2t0y2xkqtb0f7">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2105" data-mp="n9awbv834oa3">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-2106" data-mp="2nyi67cm3fccv">
            <span className="w-1.5 h-1.5 rounded-full bg-emerald-500" data-id="element-2107" data-mp="2suyty5pdropf" />
            <p className="text-[10px] font-bold text-gray-900" data-id="element-2108" data-mp="2wxc02v81homq">Custom Pitch</p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2109" data-mp="2wzomploqsn64">
            Add your rate and notes directly to the submission to win the deal.
          </p>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredMediaKitMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2110" data-mp="3bt1los9bbzqx">
      <div className="absolute -inset-6 bg-gradient-to-tr from-purple-100/40 via-zano-lavender/30 to-blue-100/20 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2111" data-mp="3glkyvo438sv7" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2112" data-mp="3kmeruvwe0xuy">
        <WindowBar title="Public Media Kit" data-id="element-2113" data-mp="3ul3z7uqnqzwy" />

        <div className="p-4 border-b border-gray-100 bg-gray-50/50 flex justify-between items-center" data-id="element-2114" data-mp="3ulvvz8vzr3ot">
          <div className="flex items-center gap-3" data-id="element-2115" data-mp="8l7z8j8qi33r">
            <div className="w-10 h-10 bg-gray-900 text-white rounded-lg flex items-center justify-center text-xs font-bold shadow-sm" data-id="element-2116" data-mp="3ktnor7xcb0z">
              ABC
            </div>
            <div data-id="element-2117" data-mp="3usxum0fq3rz9">
              <h3 className="text-sm font-bold text-gray-900" data-id="element-2118" data-mp="3qp9m9fj9oscy">
                ABC Talent Agency
              </h3>
              <p className="text-[11px] text-gray-500" data-id="element-2119" data-mp="rao1bb02i3ma">Exclusive Roster</p>
            </div>
          </div>
          <button className="flex items-center gap-1.5 bg-white border border-gray-200 text-gray-700 px-3 py-1.5 rounded-md text-[11px] font-bold shadow-sm" data-id="element-2120" data-mp="marvr7bq244m">
            <LinkIcon size={12} data-id="element-2121" data-mp="hhyrslo2uztl" /> Share Link
          </button>
        </div>

        <div className="p-4 flex-1 bg-white" data-id="element-2122" data-mp="di82yni42trp">
          <div className="flex items-center gap-2 mb-3" data-id="element-2123" data-mp="1a0dww0bnmtf9">
            <div className="relative flex-1" data-id="element-2124" data-mp="1a1xbznlkqyfg">
              <Search size={12} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-gray-400" data-id="element-2125" data-mp="159dwgl3btt80" />
              <input type="text" placeholder="Brands can search your roster..." className="w-full pl-7 pr-2 py-1.5 border border-gray-200 rounded-md text-[10px] bg-gray-50" readOnly data-id="element-2126" data-mp="2zijechj98bc1" />
            </div>
            <button className="flex items-center gap-1 border border-gray-200 bg-white text-gray-600 px-2 py-1.5 rounded-md text-[10px] font-medium" data-id="element-2127" data-mp="2upq9ltnsd2zy">
              <Filter size={10} data-id="element-2128" data-mp="2lqd0j23kqgfr" /> Filters
            </button>
          </div>

          <div className="border border-gray-100 rounded-xl overflow-hidden" data-id="element-2129" data-mp="2loa0rqcmd8xu">
            <table className="w-full text-left text-[11px] whitespace-nowrap" data-id="element-2130" data-mp="26unbev33dn6t">
              <thead className="bg-gray-50 border-b border-gray-100 text-gray-500 uppercase" data-id="element-2131" data-mp="2bt8a2q2p3yt5">
                <tr data-id="element-2132" data-mp="52sclg1xpd60">
                  <th className="px-3 py-2 font-semibold" data-id="element-2133" data-mp="528t3uwmygpv">Creator</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-2134" data-mp="a12a3xb4spus">Niche</th>
                  <th className="px-3 py-2 font-semibold" data-id="element-2135" data-mp="1uiuno2o2qpbw">
                    <TTIco data-id="element-2136" data-mp="1uje39cq951mr" />
                  </th>
                  <th className="px-3 py-2 font-semibold text-right" data-id="element-2137" data-mp="1pk1gst3alwkp">Rates</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 bg-white" data-id="element-2138" data-mp="248xsr18gztql">
                {CREATORS.slice(0, 4).map((c, i) => <tr key={i} className="hover:bg-gray-50" data-id="element-2139" data-mp="1zgd0431avmon" data-mpi={i}>
                    <td className="px-3 py-2 font-medium text-gray-900 flex items-center gap-2" data-id="element-2140" data-mp="2d8t353u7bkpk">
                      <img src={c.photo} alt={c.name} className="w-6 h-6 rounded-full object-cover bg-gray-200" data-id="element-2141" data-mp="298abpaw80gnw" />
                      {c.name}
                    </td>
                    <td className="px-3 py-2 text-gray-600" data-id="element-2142" data-mp="2i6mai078qkrf">{c.niche}</td>
                    <td className="px-3 py-2 text-gray-900 font-medium" data-id="element-2143" data-mp="su0wp7dvc49q">
                      {c.fol}
                    </td>
                    <td className="px-3 py-2 text-right" data-id="element-2144" data-mp="nwr27dbmh2f1">
                      <span className="text-[10px] font-semibold text-emerald-600" data-id="element-2145" data-mp="20h2pae2kua5">
                        View
                      </span>
                    </td>
                  </tr>)}
              </tbody>
            </table>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.3
    }} className="absolute -top-4 -right-4 sm:-right-6 z-20" data-id="element-2146" data-mp="gp3ej24hn3wm">
        <motion.div {...floatY(0.2)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2147" data-mp="3u51ffi2ulu96">
          <div className="flex items-center gap-1.5 mb-1" data-id="element-2148" data-mp="14htpp94khj5u">
            <Globe size={12} className="text-blue-500" data-id="element-2149" data-mp="19dr61fftypim" />
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2150" data-mp="2twuyafnrbh5m">
              Public & Shareable
            </p>
          </div>
          <p className="text-[10px] text-gray-500 leading-snug" data-id="element-2151" data-mp="2oxi0izvckpo9">
            Send your live media kit to brands. Built-in filters help you upsell
            more talent.
          </p>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.5
    }} className="absolute -bottom-4 -left-4 sm:-left-6 z-20" data-id="element-2152" data-mp="2f75jviqnlgne">
        <motion.div {...floatY(0.8)} className="flex items-center gap-2 bg-gray-900 text-white rounded-full border border-gray-700 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.3)] px-3 py-2" data-id="element-2153" data-mp="1w9m6k2x5xbv6">
          <CheckCircle2 size={14} className="text-emerald-400" data-id="element-2154" data-mp="3qmu6g8zzwdno" />
          <span className="text-[11px] font-medium" data-id="element-2155" data-mp="mbsn7wzxo9fo">
            Link copied to clipboard
          </span>
        </motion.div>
      </motion.div>
    </div>;
};
export const LayeredProfileOwnershipMockup = () => {
  return <div className="relative w-full text-left" data-id="element-2156" data-mp="clhb3n63itxp">
      <div className="relative h-[600px] sm:h-[440px] md:h-[420px]" data-id="element-2157" data-mp="ck61h6pyyryo">
        <div className="absolute inset-0 overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-float" data-id="element-2158" data-mp="7lus0x3l6jgz">
          <WindowBar title="Creator Profile" subtitle="Live profile" data-id="element-2159" data-mp="3mvs5qwppcwe" />

          <div className="border-b border-gray-100 p-4" data-id="element-2160" data-mp="3uqkgza3vz9z6">
            <div className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between" data-id="element-2161" data-mp="3utyh0bp2v73g">
              <div className="flex min-w-0 items-center gap-3" data-id="element-2162" data-mp="3puc5c6owdywr">
                <img src="https://cdn.magicpatterns.com/patterns/figma-images/HPPMfQF9zbBNm5oHh69dtz/3867-596.png" alt="Michelle Brown" className="h-14 w-14 shrink-0 rounded-full object-cover ring-2 ring-white shadow-sm" data-id="element-2163" data-mp="3kuz8qu885dgz" />
                <div className="min-w-0" data-id="element-2164" data-mp="1riae7a5bkx1g">
                  <div className="flex flex-wrap items-center gap-1.5" data-id="element-2165" data-mp="1vknidu4f4mw4">
                    <h3 className="text-sm font-bold text-gray-900" data-id="element-2166" data-mp="1viuc4fxpw4on">
                      Michelle Brown
                    </h3>
                    <BadgeCheck size={14} className="shrink-0 text-zano-pink" aria-label="Verified profile" data-id="element-2167" data-mp="25agr8nd6enwo" />
                  </div>
                  <div className="mt-1 flex items-center gap-2 text-[10px] font-medium text-gray-600" data-id="element-2168" data-mp="1cl9kj13kis0c">
                    <TTIco data-id="element-2169" data-mp="1hkmhieo5lfgn" />
                    <span data-id="element-2170" data-mp="1hjb37kx30pba">2.2M</span>
                    <IGIco data-id="element-2171" data-mp="1mjz3yyb44kip" />
                    <span data-id="element-2172" data-mp="1mk8viqbt2zqi">158K</span>
                  </div>
                  <p className="mt-1 text-[10px] text-gray-500" data-id="element-2173" data-mp="1mhmcnujgsxsz">
                    Fashion & lifestyle creator · Los Angeles
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-zano-pink/25 bg-zano-blush/60 px-3 py-2 sm:min-w-[176px]" data-id="element-2174" data-mp="2y18n1jqbxajh">
                <div className="flex items-center gap-1.5 text-[9px] font-bold uppercase tracking-wider text-zano-pink" data-id="element-2175" data-mp="28ctc2j4dmusm">
                  <Building2 size={11} data-id="element-2176" data-mp="2i9pq14d1o94k" /> Represented by
                </div>
                <p className="mt-1 text-[11px] font-bold text-gray-900" data-id="element-2177" data-mp="2i6tpd89tb6cf">
                  Clicks Talent
                </p>
                <span className="mt-1 inline-flex items-center gap-1 text-[9px] font-semibold text-emerald-700" data-id="element-2178" data-mp="1pjzb4t3l937e">
                  <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" data-id="element-2179" data-mp="1kmgyarmga3u4" />{' '}
                  Exclusive representation
                </span>
              </div>
            </div>
          </div>

          <div className="flex h-9 items-center gap-5 border-b border-gray-100 bg-gray-50/70 px-4 text-[10px] font-semibold uppercase tracking-wide" data-id="element-2180" data-mp="1uikbqtmr5owd">
            <span className="flex h-full items-center border-b-2 border-zano-pink text-zano-pink" data-id="element-2181" data-mp="16roaz70bjjcz">
              Social media
            </span>
            <span className="text-gray-400" data-id="element-2182" data-mp="3di03y9r9iytm">Details</span>
            <span className="text-gray-400" data-id="element-2183" data-mp="3dggnai1p2lrb">Notes</span>
          </div>

          <div className="grid gap-3 p-3 sm:grid-cols-[1.1fr_0.9fr]" data-id="element-2184" data-mp="3iab154umc6ih">
            <div className="min-w-0" data-id="element-2185" data-mp="33l551pr2uwet">
              <p className="mb-2 text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2186" data-mp="33i0t267dewdl">
                Content style
              </p>
              <div className="flex flex-wrap gap-1.5" data-id="element-2187" data-mp="2vf9aamrq6l">
                {['TikTok Shop', 'GRWM', 'Try-on hauls'].map(tag => <span key={tag} className="rounded-md border border-blue-100 bg-blue-50 px-2 py-1 text-[9px] font-semibold text-blue-600" data-id="element-2188" data-mp="5155d6hi00lc">
                    {tag}
                  </span>)}
              </div>

              <div className="mt-3 border-t border-gray-100 pt-3" data-id="element-2189" data-mp="2brv0mq9jpxoq">
                <div className="mb-2 flex items-center justify-between" data-id="element-2190" data-mp="2fu9j399ayvfh">
                  <span className="flex items-center gap-1.5 text-[10px] font-bold text-gray-900" data-id="element-2191" data-mp="2fs6gzquvlbue">
                    <TTIco data-id="element-2192" data-mp="2w3gzvlrejb75" /> TikTok
                  </span>
                  <span className="text-[9px] text-gray-500" data-id="element-2193" data-mp="2ra5qbcv7ggbj">
                    @michellethereal
                  </span>
                </div>
                <div className="grid grid-cols-3 gap-1.5" data-id="element-2194" data-mp="2hcrks8atlby0">
                  {[['Followers', '2.2M'], ['Avg. views', '50K'], ['Engagement', '5.3%']].map(([label, value]) => <div key={label} className="rounded-md border border-gray-100 bg-gray-50 px-2 py-2" data-id="element-2195" data-mp="3a1yi53yjfvho">
                      <p className="text-[8px] font-medium text-gray-500" data-id="element-2196" data-mp="311ihs3d3d4h1">
                        {label}
                      </p>
                      <p className="mt-0.5 text-[11px] font-bold text-gray-900" data-id="element-2197" data-mp="1toqgxcol6jox">
                        {value}
                      </p>
                    </div>)}
                </div>
              </div>

              <div className="mt-3 border-t border-gray-100 pt-3" data-id="element-2198" data-mp="51q1vv8fnf2b">
                <p className="mb-2 text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2199" data-mp="95o2699huvwy">
                  Audience snapshot
                </p>
                <div className="flex items-end gap-2" data-id="element-2200" data-mp="iuo523r4abgf">
                  {[34, 72, 48, 24, 18].map((height, index) => <div key={height} className="flex flex-1 flex-col items-center gap-1" data-id="element-2201" data-mp="nuu25zg5oq5w" data-mpi={index}>
                      <div className="w-full max-w-[14px] rounded-t-sm bg-teal-500/75" style={{
                    height
                  }} data-id="element-2202" data-mp="suyj1qkz7mwn" />
                      <span className="text-[7px] text-gray-400" data-id="element-2203" data-mp="2ymlo3bo21qzn">
                        {['18–24', '25–34', '35–44', '45–54', '55+'][index]}
                      </span>
                    </div>)}
                </div>
              </div>
            </div>

            <aside className="rounded-xl border border-gray-200 bg-gray-50/70 p-3" aria-label="Talent agency contact information" data-id="element-2204" data-mp="5rljj0tdt3t5">
              <div className="flex items-start justify-between gap-2" data-id="element-2205" data-mp="jso8blbrgs7r">
                <div data-id="element-2206" data-mp="ericvfqpuquj">
                  <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2207" data-mp="eqyvc04za4h1">
                    Talent agency
                  </p>
                  <p className="mt-1 text-[11px] font-bold text-gray-900" data-id="element-2208" data-mp="oqzixy9vr6r1">
                    Clicks Talent
                  </p>
                </div>
                <BadgeCheck size={15} className="text-emerald-600" aria-label="Exclusive representation" data-id="element-2209" data-mp="12h304atwka9m" />
              </div>
              <div className="mt-3 border-t border-gray-200 pt-3" data-id="element-2210" data-mp="1cgbmahij0klf">
                <p className="text-[9px] font-bold uppercase tracking-wider text-gray-400" data-id="element-2211" data-mp="17i0c25obk82o">
                  Talent manager
                </p>
                <p className="mt-1 text-[11px] font-semibold text-gray-900" data-id="element-2212" data-mp="1r0wjg5uj26tn">
                  John Marcus
                </p>
                <p className="mt-0.5 text-[9px] text-gray-500" data-id="element-2213" data-mp="1h4tfqvk1204l">
                  Partnerships lead
                </p>
              </div>
              <div className="mt-3 rounded-md border border-zano-pink/20 bg-white px-2 py-2" data-id="element-2214" data-mp="1m2cz29vl133e">
                <div className="flex min-w-0 items-center gap-1.5" data-id="element-2215" data-mp="18bhkqi2bdtsf">
                  <Mail size={11} className="shrink-0 text-zano-pink" data-id="element-2216" data-mp="1d3hratcw3kxz" />
                  <span className="truncate text-[9px] font-semibold text-gray-700" data-id="element-2217" data-mp="1d4svuve5swq5">
                    john@clickstalent.com
                  </span>
                  <Copy size={10} className="ml-auto shrink-0 text-gray-400" aria-hidden="true" data-id="element-2218" data-mp="13dy63h1pfezy" />
                </div>
              </div>
              <p className="mt-2 text-[8px] leading-relaxed text-gray-500" data-id="element-2219" data-mp="teh3x9lnvj8q">
                Brands contact this manager directly for bookings and rates.
              </p>
            </aside>
          </div>
        </div>

        <motion.div initial={{
        opacity: 0,
        y: 14
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.25
      }} className="absolute -left-3 -top-3 z-20 hidden md:block" data-id="element-2220" data-mp="14tbwkyrc9zib">
          <motion.div {...floatY(0.2, 5)} className="w-48 rounded-xl border border-gray-200 bg-white p-3 shadow-[0_16px_34px_-14px_rgba(0,0,0,0.18)]" data-id="element-2221" data-mp="m32mz3t835gl">
            <div className="flex items-center gap-2" data-id="element-2222" data-mp="c4mtwvamxm4g">
              <span className="flex h-7 w-7 items-center justify-center rounded-lg bg-zano-blush" data-id="element-2223" data-mp="77wfsdie50lo">
                <Building2 size={13} className="text-zano-pink" data-id="element-2224" data-mp="35t1zi891jx8" />
              </span>
              <div data-id="element-2225" data-mp="2etxxn41afb9e">
                <p className="text-[10px] font-bold text-gray-900" data-id="element-2226" data-mp="2esd3hfivxuc2">
                  Agency-owned profile
                </p>
                <p className="text-[9px] text-gray-500" data-id="element-2227" data-mp="2it6qm6oi753w">
                  Representation stays visible
                </p>
              </div>
            </div>
          </motion.div>
        </motion.div>

        <motion.div initial={{
        opacity: 0,
        y: 16
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.45,
        delay: 0.45
      }} className="absolute -bottom-5 -right-3 z-20 hidden md:block" data-id="element-2228" data-mp="2sseazdtno00i">
          <motion.div {...floatY(0.9, 6)} className="w-56 rounded-xl border border-zano-pink/25 bg-white p-3 shadow-[0_18px_38px_-14px_rgba(0,0,0,0.2)]" data-id="element-2229" data-mp="29weje44layhg">
            <p className="mb-1 flex items-center gap-1.5 text-[10px] font-bold text-gray-900" data-id="element-2230" data-mp="1hc64j1b3s4jk">
              <Mail size={11} className="text-zano-pink" data-id="element-2231" data-mp="r4wiw9unwohi" /> Direct manager
              contact
            </p>
            <p className="text-[9px] leading-relaxed text-gray-500" data-id="element-2232" data-mp="w13qe72pi81k">
              Every profile routes brands to the agency team—not the creator.
            </p>
          </motion.div>
        </motion.div>
      </div>

      <div className="mt-3 grid gap-2 md:hidden" data-id="element-2233" data-mp="8dlymbjfz9yl">
        <div className="flex items-center gap-2 rounded-lg border border-gray-200 bg-white p-3 shadow-sm" data-id="element-2234" data-mp="8cu1uxe3z66q">
          <span className="flex h-7 w-7 shrink-0 items-center justify-center rounded-lg bg-zano-blush" data-id="element-2235" data-mp="d5wvf5ivyfmx">
            <Building2 size={13} className="text-zano-pink" data-id="element-2236" data-mp="3umn6cgxxzewt" />
          </span>
          <p className="text-[10px] font-semibold text-gray-700" data-id="element-2237" data-mp="3fan7wdi0re9">
            Agency-owned profile: representation stays visible.
          </p>
        </div>
        <div className="flex items-center gap-2 rounded-lg border border-zano-pink/25 bg-white p-3 shadow-sm" data-id="element-2238" data-mp="3qj8l9bv5c8ci">
          <Mail size={13} className="shrink-0 text-zano-pink" data-id="element-2239" data-mp="36wx0qeetramr" />
          <p className="text-[10px] font-semibold text-gray-700" data-id="element-2240" data-mp="36zja8jla007m">
            Direct manager contact is attached to every creator.
          </p>
        </div>
      </div>
    </div>;
};
export const LayeredDiscoverabilityMockup = () => {
  return <div className="relative h-[440px] w-full" data-id="element-2241" data-mp="3bw9xprzety32">
      <div className="absolute -inset-6 bg-gradient-to-tr from-amber-100/40 via-zano-pink/20 to-zano-lavender/30 rounded-[2.5rem] blur-2xl -z-10" data-id="element-2242" data-mp="3bymgu8gwmehp" />
      <div className="absolute inset-0 bg-white rounded-2xl border border-gray-200 shadow-float overflow-hidden flex flex-col text-left" data-id="element-2243" data-mp="3kxzoqwpdqv0b">
        <WindowBar title="Brand View: Roster Database" data-id="element-2244" data-mp="3fwy3t3ozs6e" />

        <div className="p-4 bg-gray-50/50 border-b border-gray-100" data-id="element-2245" data-mp="3umrc0rjhwr4w">
          <div className="relative flex-1" data-id="element-2246" data-mp="chee7bh3ntv3">
            <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-zano-pink" data-id="element-2247" data-mp="ci69el6sbpku" />
            <div className="w-full pl-9 pr-3 py-2 border-2 border-zano-pink/40 rounded-lg text-xs font-medium text-gray-900 bg-white shadow-sm" data-id="element-2248" data-mp="7ggvmxs6gxt2">
              fashion
            </div>
          </div>
          <div className="flex flex-wrap items-center gap-1.5 mt-2.5" data-id="element-2249" data-mp="367075ggowq30">
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-2250" data-mp="3a9esq8bqu3xz">
              <Globe size={10} className="text-gray-400" data-id="element-2251" data-mp="3k1aovdu9rjzk" /> Location:
              <span className="font-semibold text-gray-900" data-id="element-2252" data-mp="3k35bcwl6vk5r">United States</span>
            </span>
            <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full text-[10px] font-medium bg-white border border-gray-200 text-gray-700" data-id="element-2253" data-mp="3p1p014r63a1t">
              Platform: <TTIco data-id="element-2254" data-mp="1tuyb5zc6fhc" />
              <span className="font-semibold text-gray-900" data-id="element-2255" data-mp="1t22h01x1adz">TikTok</span>
            </span>
          </div>
        </div>

        <div className="p-4 space-y-3" data-id="element-2256" data-mp="23mj8jzaee36m">
          <div className="border border-zano-pink/30 bg-zano-blush/20 rounded-xl p-3 shadow-sm" data-id="element-2257" data-mp="23nki1zbrmhm8">
            <div className="flex items-center gap-3 mb-3" data-id="element-2258" data-mp="1w41tldjr5w69">
              <img src="https://images.unsplash.com/photo-1534528741775-53994a69daeb?auto=format&fit=crop&w=60&h=60" className="w-12 h-12 rounded-full object-cover" alt="" data-id="element-2259" data-mp="1rca476cc1whp" />
              <div data-id="element-2260" data-mp="1icn02cs7ycky">
                <h4 className="text-sm font-bold text-gray-900" data-id="element-2261" data-mp="1id6jxz1fjb1m">
                  Elena Rodriguez
                </h4>
                <p className="text-[10px] text-gray-500" data-id="element-2262" data-mp="2ezrwr43f8ylq">Los Angeles, CA</p>
              </div>
            </div>
            <div className="flex items-center justify-between bg-white border border-gray-200 rounded-md px-2.5 py-2" data-id="element-2263" data-mp="2a0ygj3wr2thv">
              <div className="flex items-center gap-2" data-id="element-2264" data-mp="212d1hiqhlwlu">
                <div className="w-6 h-6 bg-gray-900 text-white rounded flex items-center justify-center text-[8px] font-bold" data-id="element-2265" data-mp="211tnue7vdudm">
                  ABC
                </div>
                <span className="text-[11px] font-bold text-gray-900" data-id="element-2266" data-mp="plwodw2ens25">
                  ABC Talent Agency
                </span>
              </div>
              <button className="bg-zano-pink text-white px-3 py-1 rounded text-[10px] font-bold" data-id="element-2267" data-mp="2dht3mv5nzu4k">
                Contact Manager
              </button>
            </div>
          </div>
        </div>
      </div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.4
    }} className="absolute -top-4 -right-3 sm:-right-5 z-20" data-id="element-2268" data-mp="28k9o7rx8dbb1">
        <motion.div {...floatY(0.3, 6)} className="flex items-center gap-2 bg-white rounded-xl border border-gray-200 shadow-[0_15px_35px_-12px_rgba(0,0,0,0.15)] p-3 w-48" data-id="element-2269" data-mp="2n95ubjfq8e90">
          <div className="w-8 h-8 rounded-full bg-blue-50 flex items-center justify-center shrink-0" data-id="element-2270" data-mp="1bsz3b99kjwpq">
            <Users size={14} className="text-blue-600" data-id="element-2271" data-mp="1gra998wqtn2o" />
          </div>
          <div data-id="element-2272" data-mp="18b1hlg0dm968">
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2273" data-mp="18ajga4yux4rg">
              Get Discovered
            </p>
            <p className="text-[9px] text-gray-500" data-id="element-2274" data-mp="14cu18m9tr9vk">
              Brands search the database daily.
            </p>
          </div>
        </motion.div>
      </motion.div>

      <motion.div initial={{
      opacity: 0,
      y: 20
    }} whileInView={{
      opacity: 1,
      y: 0
    }} viewport={{
      once: true
    }} transition={{
      duration: 0.5,
      delay: 0.6
    }} className="absolute bottom-6 -left-4 sm:-left-6 z-20" data-id="element-2275" data-mp="1dcqwj6qpz6zl">
        <motion.div {...floatY(1.1)} className="bg-white rounded-xl border border-gray-200 shadow-[0_20px_40px_-15px_rgba(0,0,0,0.15)] p-3 w-56" data-id="element-2276" data-mp="ukfyprax9z8c">
          <div className="flex items-center gap-2 mb-2" data-id="element-2277" data-mp="6t0t9pnun1sv">
            <div className="p-1.5 bg-zano-blush rounded-md" data-id="element-2278" data-mp="1vhfsryz6t1z">
              <Mail size={12} className="text-zano-pink" data-id="element-2279" data-mp="21ys4cu24y07e" />
            </div>
            <p className="text-[11px] font-bold text-gray-900" data-id="element-2280" data-mp="21wpbm29llsyz">
              New Inbound Inquiry
            </p>
          </div>
          <div className="bg-gray-50 border border-gray-100 rounded p-2" data-id="element-2281" data-mp="2bntieep4e7iq">
            <p className="text-[10px] text-gray-600 line-clamp-2" data-id="element-2282" data-mp="2fpx55zh8oyjb">
              "Hi, we saw Elena in the Zano database and would love to book her
              for our upcoming campaign..."
            </p>
          </div>
        </motion.div>
      </motion.div>
    </div>;
};